import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateColumnVisibility, reorderColumn } from '../redux/reducers/drmCustomColumnSlice';

export const useDrmColumnManagement = (viewCategory, defaultColumns, customViews) => {
  const dispatch = useDispatch();
  const { checkedColumn } = useSelector(state => state.customColumn);

  const currentView = useMemo(() => {
    const checked = checkedColumn[viewCategory];
    if (checked) {
      return checked;
    }
    
    const bookmarked = customViews?.find(view => view.isBookMark);
    if (bookmarked) {
      return bookmarked;
    }
    
    return null;
  }, [checkedColumn, viewCategory, customViews]);

  const visibleColumns = useMemo(() => {
    if (!currentView || !currentView.tableDetails || currentView.tableDetails.length === 0) {
      return defaultColumns.map(col => col.columnName);
    }
    return currentView.tableDetails.map(col => col.columnName);
  }, [currentView, defaultColumns]);

  const orderedColumns = useMemo(() => {
    if (!currentView || !currentView.tableDetails || currentView.tableDetails.length === 0) {
      return defaultColumns.map((col, index) => ({
        columnName: col.columnName,
        priority: col.columnNumber || index + 1,
        isVisible: true
      }));
    }

    const visibleMap = new Map(
      currentView.tableDetails.map(col => [col.columnName, col.priority])
    );

    return defaultColumns.map((col, index) => ({
      columnName: col.columnName,
      priority: visibleMap.get(col.columnName) || (index + 1),
      isVisible: visibleMap.has(col.columnName)
    })).sort((a, b) => {
      if (a.isVisible && !b.isVisible) return -1;
      if (!a.isVisible && b.isVisible) return 1;
      if (a.isVisible && b.isVisible) return a.priority - b.priority;
      return a.priority - b.priority;
    });
  }, [currentView, defaultColumns]);

  const isColumnVisible = useCallback((columnName) => {
    if (!currentView || !currentView.tableDetails || currentView.tableDetails.length === 0) {
      return defaultColumns?.some(col => col.columnName === columnName);
    }
    return currentView.tableDetails.some(col => col.columnName === columnName);
  }, [currentView, defaultColumns]);

  const priorityOrder = useCallback((columnName) => {
    if (!currentView || !currentView.tableDetails || currentView.tableDetails.length === 0) {
      const col = defaultColumns.find(col => col.columnName === columnName);
      return col ? col.columnNumber : null;
    }
    const col = currentView.tableDetails.find(col => col.columnName === columnName);
    return col ? col.priority : null;
  }, [currentView, defaultColumns]);

  const toggleColumnVisibility = useCallback((columnName) => {
    const isVisible = isColumnVisible(columnName);
    dispatch(updateColumnVisibility({
      viewCategory,
      columnName,
      isVisible: !isVisible
    }));
  }, [viewCategory, isColumnVisible]);

  const handleColumnReorder = useCallback((columnName, direction) => {
    dispatch(reorderColumn({
      viewCategory,
      columnName,
      direction
    }));
  }, [viewCategory]);

  const allColumnsWithStatus = useMemo(() => {
    return defaultColumns.map(col => ({
      columnName: col.columnName,
      columnNumber: col.columnNumber,
      isVisible: isColumnVisible(col.columnName),
      priority: priorityOrder(col.columnName)
    }));
  }, [defaultColumns, isColumnVisible, priorityOrder]);

  return {
    bookmarkedView: currentView,
    visibleColumns,
    orderedColumns,
    isColumnVisible,
    toggleColumnVisibility,
    handleColumnReorder,
    allColumnsWithStatus
  };
};